import type { FooterPost, SocialLink, Text } from '@kkf/design'
import { appUrl } from '@kkf/design'
import type { MessageKey } from '../i18n'
import { POSTS } from './blog'
import { LINKS } from './links'

/**
 * Content for the shared shell. Defined exactly once — the header, footer, sidebar and
 * search modal are the same on every page, so there is no per-page variation to model.
 *
 * Images are referenced from the web root (`/assets/img/...`). A relative path would
 * break on any nested route such as `/services/clean-water-supply.
 */

/**
 * This app's lockup: the Foundation's lion and "KING OF KINGS" over "FOUNDATION".
 *
 * Every app had `logo.png` until 2026-09-19 — the FOUNDATION's artwork, strapline and all —
 * and set its venture's name as a separate word beneath it, so the mark named the charity
 * and the chrome named the venture. There is now one lockup per app, generated from a
 * single construction by `tools/logo/build-lockups.py`; see that script and
 * `tools/logo/reference/README.md`.
 *
 * `LOGO` is for pale chrome, which is every surface this app has. `LOGO_WHITE` is the same
 * mark for a dark ground.
 *
 * The Foundation's own mark drops "For lives worth living", which `logo.png` carries. At
 * the ~40px the header gives it that line is a grey smudge, and it is on the page in words
 * a dozen times over. `assets/media/logo.png` still holds the full artwork.
 */
export const LOGO = '/assets/img/logo-foundation.svg'
export const LOGO_WHITE = '/assets/img/logo-foundation-white.svg'

/**
 * The WIDE lockup — the lion beside the type rather than above it — for the header and the
 * off-canvas, which are short and wide.
 *
 * The stacked mark puts sixty per cent of its height into the lion, and the header bar is
 * clamp(76px, 4.52vw, 86px) tall: at the 74px `.logo` allows, its venture line renders
 * 2.7px, which is not small but absent. The wide one spends the bar's width instead and the
 * same word comes out near 7px. The footer and the 404 keep the stacked file — they have
 * vertical room and no width to spare.
 */
export const LOGO_WIDE = '/assets/img/logo-foundation-wide.svg'
export const LOGO_WIDE_WHITE = '/assets/img/logo-foundation-wide-white.svg'

/** The two most recent posts, for the footer's "Recent Posts" column. */
const FOOTER_POSTS: FooterPost[] = POSTS.filter((post) => post.published)
  .slice(0, 2)
  .map((post) => ({
    image: post.image,
    date: post.published ?? '',
    title: post.title,
    to: `/blog/${post.slug}`,
  }))

/**
 * The Foundation's social presence: Facebook only, and a real URL. The template shipped
 * four placeholder icons pointing at `#` in the header and a different four in the
 * footer; both lists are now this one, so there is nothing to keep in sync.
 */
export const SOCIALS: SocialLink[] = [
  { icon: 'facebook', href: 'https://www.facebook.com/profile.php?id=61592414898013', label: 'Facebook' },
]

/**
 * Primary navigation — five single links, no dropdowns.
 *
 * `HeaderNav` still supports `children`; nothing here uses it. The routes that used to
 * hang off the old "Pages", "Event" and "Blog" menus (services, projects, team,
 * pricing, faq, events, blog) are still routed and still reachable from the footer's
 * quick links — they are simply not in the header any more.
 *
 * The last three entries carry `href`, not `to`: the resort, the garage and the Partners
 * are their own apps on their own subdomains now. `HeaderNav` renders an `href` entry as
 * a plain anchor and never marks it current, which is right — another app's page is never
 * this app's current page.
 */
export const NAV: { labelKey: MessageKey; to?: string; href?: string }[] = [
  { labelKey: 'nav.home', to: '/' },
  { labelKey: 'nav.about', to: '/about' },
  // The three ventures in the order the Foundation names them. Partners sits before
  // Garage as of 2026-09-19 — it is the one a visitor can act on today, and the garage's
  // own site is the deepest of the three.
  { labelKey: 'nav.resort', href: LINKS.resort },
  { labelKey: 'nav.partners', href: LINKS.partners },
  { labelKey: 'nav.garage', href: LINKS.garage },
]

// Donation left the nav; the header's one button carries it instead, which is where a
// visitor looks for it anyway.
export const HEADER_CTA = { labelKey: 'nav.donate' as MessageKey, to: '/donations' }

export const FOOTER = {
  // Closes every page — what the Foundation actually offers churches, families and
  // young people, stated once.
  preamble: {
    subTitle: { en: 'What we offer', mn: 'Бидний санал болгож буй зүйл' } satisfies Text,
    title: {
      en: 'Churches, Families And Young People',
      mn: 'Чуулган, гэр бүл, залуучууд',
    } satisfies Text,
    paragraphs: [
      {
        en: 'Through donations and services, churches can hold seminars, retreats, and events, while churches and families can find a peaceful place to rest, reconnect, and seek God’s presence.',
        mn: 'Хандив, үйлчилгээгээр дамжуулан чуулгангууд семинар, сүнслэг цугларалт, арга хэмжээ зохион байгуулж чадах бөгөөд чуулган, гэр бүлүүд амарч, дахин холбогдож, Бурханы оршихуйг эрэлхийлэх амгалан газар олж чадна.',
      },
      {
        en: 'Young people can receive mentorship, guidance, membership opportunities, and practical skills development, with all our programs working together to strengthen churches, families, and youth.',
        mn: 'Залуучууд зөвлөгөө, чиглүүлэг, гишүүнчлэлийн боломж, практик ур чадварын хөгжлийг хүртэж чадах бөгөөд манай бүх хөтөлбөр чуулган, гэр бүл, залуучуудыг бэхжүүлэхээр хамтран ажилладаг.',
      },
    ] satisfies Text[],
    // One of the two real KKF photographs in the tree — everything else in
    // /assets/img is still a sized placeholder. It also opens the home slider.
    image: '/assets/img/banner-community-dance.jpg',
    imageAlt: {
      en: 'Community members dancing together in traditional dress at a winter gathering',
      mn: 'Өвлийн цугларалт дээр үндэсний хувцастай бүжиглэж буй олон нийтийн гишүүд',
    } satisfies Text,
  },
  // Points on to the five pages worth naming again at the end of a page. The address,
  // email and phone this card used to hold are in the Contact Us column below.
  wayfinding: {
    labelKey: 'footer.wayfinding' as MessageKey,
    links: [
      { labelKey: 'nav.about' as MessageKey, to: '/about' },
      { labelKey: 'nav.theResort' as MessageKey, href: LINKS.resort },
      { labelKey: 'nav.garage' as MessageKey, href: LINKS.garage },
      { labelKey: 'nav.partners' as MessageKey, href: LINKS.partners },
      { labelKey: 'nav.donate' as MessageKey, to: '/donations' },
    ],
  },
  // The footer is a beige surface now, so it takes the dark logo, not the white one.
  logo: LOGO,
  homeTo: '/',
  about: {
    en: 'A Christian foundation in Mongolia, accompanying people toward a closer relationship with God through mentoring, discipleship and places of rest — for lives worth living.',
    mn: 'Монгол дахь Христийн сан — зөвлөгөө, шавь бэлтгэл, амрах газруудаар дамжуулан хүмүүсийг Бурхантай илүү дотно харилцаанд хүрэх замд нь дагалдан явдаг. Үнэ цэнтэй амьдралын төлөө.',
  } satisfies Text,
  socials: SOCIALS,
  // Carries the pages the header no longer lists, so nothing became unreachable when
  // the nav dropped to six links.
  quickLinks: {
    titleKey: 'footer.quickLinks' as MessageKey,
    links: [
      { labelKey: 'nav.aboutUs' as MessageKey, to: '/about' },
      { labelKey: 'nav.theResort' as MessageKey, href: LINKS.resort },
      { labelKey: 'nav.theLibrary' as MessageKey, href: appUrl(LINKS, 'resort', '/library') },
      { labelKey: 'nav.events' as MessageKey, to: '/events' },
      { labelKey: 'nav.blog' as MessageKey, to: '/blog' },
      { labelKey: 'nav.contact' as MessageKey, to: '/contact' },
    ],
  },
  // Derived from the posts that exist, so the footer cannot link at a slug that does not.
  /**
   * The two most recent posts.
   *
   * **Not translated, and that is the honest state.** The blog is markdown in
   * `src/content/`, one file per post, and translating a post is a piece of work with an
   * author's name on it rather than a lookup — see the session brief.
   */
  recentPosts: {
    titleKey: 'footer.recentPosts' as MessageKey,
    posts: FOOTER_POSTS,
  },
  contact: {
    titleKey: 'footer.contact' as MessageKey,
    links: [
      // The inbox is an address, not copy, and is printed as written in both languages.
      { icon: 'mail', label: 'foundation@kkfmongolia.com', href: 'mailto:foundation@kkfmongolia.com' },
      // No `href`: neither of these is something to open. The template printed
      // `123-456-7890` as a `tel:` link, which reads as a number somebody can ring.
      { icon: 'telephone-call', labelKey: 'footer.phone' as MessageKey },
      { icon: 'location', labelKey: 'footer.address' as MessageKey },
    ],
    newsletterPlaceholderKey: 'footer.newsletter' as MessageKey,
    agreementTextKey: 'footer.agreement' as MessageKey,
    privacyPolicy: { labelKey: 'legal.privacy' as MessageKey, to: '/privacy' },
  },
  copyrightNameKey: 'footer.copyright' as MessageKey,
  /*
   * The two links beside the copyright. Both pointed at `'#'` from the day the template
   * landed until 2026-09-19 — a link that scrolls to the top of the page you are on, on
   * every page of four sites. They are real routes now; the words are shared, the routes
   * are this app's. See `@kkf/design`'s `config/legal.ts`.
   */
  bottomNav: [
    { labelKey: 'legal.terms' as MessageKey, to: '/terms' },
    { labelKey: 'legal.privacy' as MessageKey, to: '/privacy' },
  ],
  vectorImage: '/assets/img/footer-vector-img.png',
}

/**
 * The off-canvas panel's footer.
 *
 * `sites` is the other three KKF sites, and **never this one** — see `MobileSidebar`'s
 * `sites` for why the list is composed here rather than derived in the design system.
 *
 * The same three places are in the footer's wayfinding column, and that is not a
 * duplication to collapse: the footer is several screens below the fold on a phone, and
 * the menu is what a phone visitor actually opens to go somewhere. One list of KKF sites
 * would mean the phone menu or the desktop footer losing its only route out.
 */
export const SIDEBAR = {
  followTitleKey: 'sidebar.follow' as MessageKey,
  socials: SOCIALS,
  sitesTitleKey: 'sidebar.sites' as MessageKey,
  sites: [
    { labelKey: 'nav.theResort' as MessageKey, href: LINKS.resort },
    { labelKey: 'nav.garage' as MessageKey, href: LINKS.garage },
    { labelKey: 'nav.partners' as MessageKey, href: LINKS.partners },
  ],
}

/**
 * The bottom tab bar, below 992px.
 *
 * **Every destination here is a placeholder** — they are five real routes of this app, in a
 * plausible order, chosen so the bar can be seen and used while the Foundation decides what
 * a phone visitor actually reaches for. Placeholder means a real route, never `'#'`: a tab
 * that goes nowhere is the same mistake as a form that appears to send.
 *
 * `primary` marks the one filled tab — the single action this app exists for, which on the
 * Foundation's site is giving. At most one; a row of five emphasised tabs emphasises none.
 *
 * The icon font has seventy glyphs drawn for a charity template and no house among them, so
 * "home" is a star. See `app_design/tests/icon-font.test.ts` — a name the font does not
 * carry renders nothing at all, with no warning anywhere.
 */
export const BOTTOM_NAV: { labelKey: MessageKey; icon: string; to: string; primary?: boolean }[] = [
  { labelKey: 'tab.home' as MessageKey, icon: 'star', to: '/' },
  { labelKey: 'tab.about' as MessageKey, icon: 'team', to: '/about' },
  { labelKey: 'tab.donate' as MessageKey, icon: 'love', to: '/donations', primary: true },
  { labelKey: 'tab.blog' as MessageKey, icon: 'comment', to: '/blog' },
]
